import { environment } from 'src/environments/environment';
import { UltimaFichadaRequested, TicketsPendientesRequested } from './../../base/home/store/recursos-humanos/recursos-humanos.actions';
import { Router } from '@angular/router';
import { idPersonaInstitucional, sessionOpen, sessionOnInit, useTokenAcceso, tokenSesion, otherSystemData, sessionIsUsuarioExterno, CambioClaveMotivoErrorEnum } from './session.selectors';
import { SessionActionTypes, SessionOpenLoaded, ValidateSessionRequested, ValidateSessionDone, RefreshSessionRequested, RefreshTokenRequested as RefreshTokenRequested, RefreshLegacySessionRequested, VerifyDateDone, SessionCreateLoaded, SessionCreateCancelled, SessionOpenCancelled, PostponePassExpirationLoaded, PostponePassExpirationCancelled, CambioClaveRequested, CambioClaveDone, CambioClaveCancelled } from './session.actions';
import { SessionService } from './../../services/session.service';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { AppState } from 'src/app/reducers';
import { withLatestFrom, mergeMap, catchError, switchMap, tap, map, filter, throttleTime, delay } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { INTRANET_REQUESTED_URL_LABEL, INTRANET_TOKEN_KEY, Session, TokenInfo, UsuarioAcceso } from 'src/app/session/store/session';
import { JwtHelperService } from '@auth0/angular-jwt';
import { EmailsRequested } from 'src/app/base/home/store/email/email.actions';
import { AllAplicacionRequested } from 'src/app/base/home/store/aplicacion/aplicacion.actions';
import { BeneficioCategoriasRequested, BeneficioPageRequested } from 'src/app/base/home/store/beneficio/beneficio.actions';
import { SalutacionPageRequested } from 'src/app/base/home/store/salutacion/salutacion.actions';
import { ToastService } from 'src/app/services/toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorCode } from './../../../common/common';
import { menuComedorRequested } from 'src/app/base/home/store/menu-comedor/menu-comedor.actions';


@Injectable()
export class SessionEffects {

	private readonly DEV_SERVERS: string[] = [ "localhost", "192.168.1.3" ];

	private readonly SECONDS_PER_MINUTE: number = 60;
	private readonly MILLIS_PER_SECOND: number = 1000;
	private readonly MILLIS_PER_MINUTE: number = this.SECONDS_PER_MINUTE * this.MILLIS_PER_SECOND;
	private readonly DATE_THRESHOLD_IN_MILLIS: number = 5 * this.MILLIS_PER_MINUTE;
	private readonly NOT_FOUND: number = 404;
	private readonly BAD_REQUEST: number = 400;

	private jwtHelper: JwtHelperService;


	doSessionCreateRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType( SessionActionTypes.SessionCreateRequested ),
			withLatestFrom( this.store.select( useTokenAcceso ), this.store.select( idPersonaInstitucional ) ),
			mergeMap( ([ action, useTokenAcceso, idPersonaInstitucional ]) => {
				let observable: Observable<Session>;
				if( useTokenAcceso )
					observable = this.service.createSessionFromToken();
				else if( idPersonaInstitucional != null )
					observable = this.service.createSessionFromIPI( idPersonaInstitucional );
				else
					observable = of( null );

				return observable
					.pipe(
						map( session => new SessionCreateLoaded( { session } ) ),
						catchError( error => of( new SessionCreateCancelled() ) )
					);
			})
		);
	});

	doSessionOpenRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType( SessionActionTypes.SessionOpenRequested ),
			withLatestFrom( this.store.select( tokenSesion ), this.store.select( sessionIsUsuarioExterno ) ),
			switchMap( ([ action, tokenSesion, isUsuarioExterno ]) => {
				let actions: Action[];
				if( tokenSesion == null ) {
					actions = [ new SessionOpenCancelled() ];
				} else {
					actions = [ new SessionOpenLoaded(), new RefreshTokenRequested( { tokenSesion } ), ...this.getInitialDataActions( isUsuarioExterno ) ];
				}
				// Una vez creada la sesión eliminó del local storage la key con la URL requerida inicialmente
				localStorage.removeItem( INTRANET_REQUESTED_URL_LABEL );
				return actions;
			}),
			catchError( error =>
				of( new SessionOpenCancelled() )
			)
		);
	});

	doRefreshSessionRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType( SessionActionTypes.RefreshSessionRequested ),
			withLatestFrom( this.store.select( otherSystemData ) ),
			mergeMap( ([action, otherSystemData]) => {
				return this.service.createUsuarioAcceso( otherSystemData.idPersonaInstitucional, otherSystemData.phpSession, otherSystemData.ce, otherSystemData.session );
			}),
			switchMap( (usuarioAcceso: UsuarioAcceso) => {
				return this.service.createSessionFromToken();
			}),
			switchMap( (session: Session) => {
 				let actions: Action[];
				if( session == null ) {
					actions = [ new SessionCreateCancelled(), new ValidateSessionDone( { valid: false } ) ];
				} else {
					actions = [ new SessionCreateLoaded( { session } ), new SessionOpenLoaded(),
						this.isDevServer()? new ValidateSessionDone( { valid: true } ): new RefreshLegacySessionRequested(),
						new RefreshTokenRequested( { tokenSesion: session.token } ), ...this.getInitialDataActions( session.usuario.isExterno ) ];
				}
				return actions;
			}),
			catchError( error =>
				of( new SessionOpenCancelled() )
			)
		);
	});

	doRefreshTokenRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<RefreshTokenRequested>( SessionActionTypes.RefreshTokenRequested ),
			tap( action => {
				let tokenSesion: string = action.payload.tokenSesion;
				localStorage.setItem( INTRANET_TOKEN_KEY, tokenSesion );
			})
		);
	}, { dispatch: false }  );

	doValidateRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<ValidateSessionRequested>( SessionActionTypes.ValidateSessionRequested ),
			withLatestFrom( this.store.select( sessionOnInit ), this.store.select( sessionOpen ), this.store.select( tokenSesion ) ),
			filter( ([ action, storeOnInit, storeOpen, storeToken ]) => !storeOnInit ),
			switchMap( ([ action, storeOnInit, storeOpen, storeToken ]) => {
				let valid: boolean = true;
				let result = null;

				// Verifico si hay sesión en el store
				if( storeOpen && storeToken ) {
					// Verifico si la sesión está activa
					valid = !this.jwtHelper.isTokenExpired( storeToken );
				} else {
					// Busco si hay sesión con el token del local storage, esto sucede cuando el usuario realiza un
					// refresh de la página
					let localStorageToken = localStorage.getItem( INTRANET_TOKEN_KEY );
					let isLocalStorageTokenUsable: boolean;
					try {
						isLocalStorageTokenUsable = localStorageToken && !this.jwtHelper.isTokenExpired( localStorageToken );
					} catch( error ) {
						isLocalStorageTokenUsable = false;
					}

					if( isLocalStorageTokenUsable ) {
						// A partir del local storage token recupero el id persona institucional y creo una nueva sesión
						let tokenInfo: TokenInfo = this.jwtHelper.decodeToken( localStorageToken );
						let idPersonaInstitucional: string = tokenInfo.ipi;
						let phpSession: string = tokenInfo.ps;
						let ce: string = tokenInfo.ce;
						let session: string = tokenInfo.se;
						if( !idPersonaInstitucional || idPersonaInstitucional == '' || !phpSession || phpSession == '' || !ce || ce == '' || !session || session == '' )
							valid = false;
						else
							result = new RefreshSessionRequested( { idPersonaInstitucional, phpSession, ce, session } );
					} else
						valid = false;
				}

				if( result == null )
					result = valid && !this.isDevServer()? new RefreshLegacySessionRequested(): new ValidateSessionDone( { valid } );

				return of( result );
			})
		);
	});

	doRefreshLegacySession$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<RefreshLegacySessionRequested>( SessionActionTypes.RefreshLegacySessionRequested ),
			throttleTime( 500 ),
			switchMap( () => this.store.select( otherSystemData ) ),
			mergeMap( otherSystemData => this.isDevServer()? of( true ): this.service.refreshLegacySession( otherSystemData.session ) ),
			map( valid => new ValidateSessionDone( { valid } ) ),
			catchError( () => of( new ValidateSessionDone( { valid: false } ) ) )
		);
	});

	doSessionExpired$ = createEffect( () => {
		return this.actions$.pipe(
			ofType( SessionActionTypes.SessionExpirationRequested, SessionActionTypes.SessionOpenCancelled, SessionActionTypes.SessionCloseRequested ),
			tap( (action: Action) => {
				// Si el token de seguridad no existe => guardo en local storage la URL a la cual quería ir el usuario
				// Si el token expiró => guardo en el local storage la URL actual así cuando vuelve a ingresar continúa navegando a partir de donde dejó
				if( action.type == SessionActionTypes.SessionExpirationRequested )
					localStorage.setItem( INTRANET_REQUESTED_URL_LABEL, window.location.href );

				localStorage.removeItem( INTRANET_TOKEN_KEY );
				window.location.replace( environment.legacyLogoutUrl );
			}),
			switchMap( () => this.service.deleteSession() )
		);
	}, { dispatch: false } );

	doSessionDisable$ = createEffect( () => {
		return this.actions$.pipe(
			ofType( SessionActionTypes.SessionDisableRequested ),
			tap( (action: Action) => {
				localStorage.removeItem( INTRANET_TOKEN_KEY );
			})
		);
	}, { dispatch: false })

	doVerifyDate$ = createEffect( () => {
		return this.actions$.pipe(
			ofType( SessionActionTypes.VerifyDateRequested ),
			switchMap( () => this.service.getServerDate() ),
			map( notificacion => {
				let serverDate: Date = notificacion.bundle[0];
				let clientDate: Date = new Date();
				let delta: number = Math.abs( serverDate.valueOf() - clientDate.valueOf() );
				return new VerifyDateDone( { validDate: delta < this.DATE_THRESHOLD_IN_MILLIS } );
			}),
			catchError( () => of( new VerifyDateDone( { validDate: false } ) ) )
		);
	});

	doPostponePassExpirationRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType( SessionActionTypes.PostponePassExpirationRequested ),
			switchMap( () => {
				return this.service.posponerAvisoExpiracionClave()
					.pipe(
						map( darAviso => new PostponePassExpirationLoaded( { darAviso } ) ),
						catchError( () => {
							this.toastService.danger( "No se pudo actualizar el recordatorio" );
							return of( new PostponePassExpirationCancelled() );
						})
					)
			})
		);
	});

	doCambioClaveRequested$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<CambioClaveRequested>( SessionActionTypes.CambioClaveRequested ),
			switchMap( action => this.service.cambiarClave( action.payload.claveActual, action.payload.claveNueva ).pipe(
				map( usuarioExpiracionClave => {
					localStorage.removeItem( INTRANET_TOKEN_KEY );	// Para evitar que el usuario pueda reestablecer sesión
					return new CambioClaveDone( { usuarioExpiracionClave } )
				}),
				catchError( response => {
					let motivo: CambioClaveMotivoErrorEnum = CambioClaveMotivoErrorEnum.UNEXPECTED_ERROR;
					// Obtengo el código de error y lo transformo en CambioClaveMotivoErrorEnum
					if( response instanceof HttpErrorResponse ) {
						let error: ErrorCode = response.error;
						motivo = CambioClaveMotivoErrorEnum[ error.errorCode ];
					}

					if( motivo != CambioClaveMotivoErrorEnum.MF_CLAVE_ACTUAL_ERROR )
						localStorage.removeItem( INTRANET_TOKEN_KEY );	// Para evitar que el usuario pueda reestablecer sesión

					return of( new CambioClaveCancelled( { motivo } ) );
				})
			))
		);
	});

	constructor( private actions$: Actions,	private store: Store<AppState>,	private service: SessionService, private router: Router, private toastService: ToastService ) {
		this.jwtHelper = new JwtHelperService();
	}

	private getInitialDataActions( isUsuarioExterno: boolean ): Action[] {
		let actions: Action[] =  [
			/* new RevistaPageRequested( { check: false } ), */
		];

		/**
	 		* El componente <self-service> se renderiza dos veces en Comunitá (para distintos tamaños de pantalla).
			* Debido a esto, si migramos estas acciones al ngOnInit() del componente, cada request se ejecutará
			* dos veces, una por cada instancia del componente. Al colocarlos en los efectos de la sesión,
			* nos aseguramos de que se ejecuten una única vez.
		**/
		if( !isUsuarioExterno )
			actions.push( new UltimaFichadaRequested(), new TicketsPendientesRequested() );

		return actions;
	}

	private isDevServer(): boolean {
		return this.DEV_SERVERS.find( serverName => serverName == window.location.hostname ) != null;
	}

}
